import { useNotification } from "@expert/common-ui";
import type { ProductInfo } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { Button, Checkbox, Flex, Text, TextInput } from "@mantine/core";
import type { JSX } from "react";
import { useState } from "react";
import { useAnalytics } from "@/analytics";
import type { EligibleProduct } from "@/sales-tools";
import { createEnrollment, useProductOfferStore } from "@/sales-tools";
import { SalesFooter } from "@/sales-tools/home-product/components/StepSpecific/SalesFooter";
import { useIntegratedFlowStore } from "@/sales-tools/home-product/stores/useIntegratedFlowStore";
import { EnrollmentStatus, SaleProgress } from "@/sales-tools/home-product/types";
import { usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { GreenQuote } from "../Icons";
import classes from "./InlineSaleTrackerStep.module.css";

const logger = getLogger({
    module: "InlineSaleTrackerStep",
    supportTeam: "sales",
});

export function InlineSaleTrackerStep({
    productInfo,
    product,
    productInfoLink,
}: {
    productInfo: ProductInfo;
    product: EligibleProduct;
    productInfoLink?: string;
}): JSX.Element | null {
    const requiresConfirmationCode = productInfo.confirmationCodePlaceholder;
    const { dispatcher } = useAnalytics();
    const sessionId = useSessionId();
    const { voiceTask } = useTypedMostRecentTask();
    const partner = usePartner();
    const [confirmationCodeValue, setConfirmationCodeValue] = useState("");
    const [checked, setChecked] = useState(false);
    const notification = useNotification();

    const isValidConfirmationCode = product.confirmationCodePattern
        ? new RegExp(product.confirmationCodePattern).test(confirmationCodeValue)
        : true;

    const onAccept = async () => {
        void dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: product.productSku,
            confirmationCode: confirmationCodeValue,
        });

        const enroll = {
            callId: voiceTask?.agentCallId ?? sessionId,
            sessionId,
            partner,
            productOfferId: product.id,
            confirmationCode: confirmationCodeValue,
            productSku: product.productSku,
        };

        const response = await createEnrollment(enroll);

        if (response.enrollmentStatus === EnrollmentStatus.Success) {
            useProductOfferStore.getState().updateProduct(sessionId, product.id, {
                ...product,
                saleProgress: SaleProgress.Success,
            });
        } else {
            logger.error("There was an issue processing the enrollment");
            notification.error("There was an error processing the enrollment");
        }
    };

    return (
        <Flex direction={"column"} justify={"space-between"}>
            <Flex mt="1.5rem" px={24} gap={24} direction="column" w="100%">
                <Text fz="h5" fw="700">
                    Track sale
                </Text>
                <Flex direction="column" gap="xs">
                    <Text size="xs">Please ask:</Text>
                    <Flex gap="xs">
                        <Flex>
                            <GreenQuote />
                        </Flex>
                        <Text size="xs">{productInfo.confirmationCodeTitle}</Text>
                    </Flex>
                </Flex>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                    }}
                >
                    {requiresConfirmationCode ? (
                        <TextInput
                            className="fs-mask"
                            classNames={{ input: classes.textAreaStyle }}
                            placeholder={productInfo.confirmationCodePlaceholder}
                            size="xs"
                            onChange={(event) => {
                                setConfirmationCodeValue(event.currentTarget.value);
                            }}
                            defaultValue={confirmationCodeValue}
                        />
                    ) : (
                        <Checkbox
                            label={
                                <Text c="dark.1" size="xs">
                                    {productInfo.confirmationCheckboxText}
                                </Text>
                            }
                            checked={checked}
                            onChange={(event) => setChecked(event.currentTarget.checked)}
                        />
                    )}
                    {productInfoLink && (
                        <Button
                            onClick={() => window.open(productInfoLink, "_blank")?.focus()}
                            classNames={{ root: classes.linkButton }}
                            size="xxs"
                            p={0}
                            variant="transparent"
                        >
                            <Text size="xs" td="underline">
                                View product page
                            </Text>
                        </Button>
                    )}
                </form>
            </Flex>
            <SalesFooter
                // perform enrollment
                continueButtonProps={{
                    text: "Confirm",
                    onClick: onAccept,
                    isDisabled: requiresConfirmationCode ? !isValidConfirmationCode : !checked,
                }}
                product={product}
                onBack={() =>
                    productInfo.salesOfferChecklist
                        ? useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SendLinkStep")
                        : useProductOfferStore.getState().updateSelectedProduct(sessionId, undefined)
                }
            />
        </Flex>
    );
}
